import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useIdentity } from "../../auth/useIdentity";
import { getContactDetails } from "../../api/account";
import { getSetupStatus, setupNotices, saveLoginEmailForInvoices } from "../../api/operatorSetup";
import { getGatewayAssignments } from "../../api/paymentGateway";

/**
 * What the operator still has to do, on its dashboard (owner 2026-10-31: "so
 * that after first login operator gear up"). A read that fails raises no
 * notice: a false "pending" is worse than a missing one.
 */
export function SetupNotices({ activePlans }: { activePlans?: number | undefined }) {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);
  const status = useQuery({ queryKey: ["setup", ns], enabled, retry: false, queryFn: ({ signal }) => getSetupStatus(identity, ns, signal) });
  const tenant = status.data?.tenant === true;
  const gateway = useQuery({ queryKey: ["setup", "gateway", ns], enabled: enabled && tenant, retry: false, queryFn: ({ signal }) => getGatewayAssignments(identity, ns, signal) });
  const contact = useQuery({ queryKey: ["setup", "contact", ns], enabled: enabled && tenant, retry: false, queryFn: ({ signal }) => getContactDetails(identity, ns, signal) });
  const saveEmail = useMutation({
    mutationFn: () => saveLoginEmailForInvoices(identity, ns),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["setup", ns] }),
  });
  if (!status.data) return null;
  const notices = setupNotices(status.data, { gateway: gateway.data, activePlans, loginEmailVerified: contact.data?.emailVerified });
  if (notices.length === 0) return null;
  return (
    <Alert severity="warning" data-testid="setup-notices">
      <AlertTitle>Finish setting up your business</AlertTitle>
      <Stack spacing={1}>
        {notices.map((n) => (
          <Stack key={n.key} direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <span>
              {n.key === "email" && n.action === "use-login-email" && contact.data?.email
                ? `We can send your invoices to ${contact.data.email}.`
                : n.text}
            </span>
            {n.to && <Button size="small" component={RouterLink} to={n.to}>{n.action}</Button>}
            {n.action === "use-login-email" && contact.data?.email && (
              <Button size="small" disabled={saveEmail.isPending} onClick={() => saveEmail.mutate()}>Use this email</Button>
            )}
          </Stack>
        ))}
        {saveEmail.isError && <span>Could not save your invoice email. Try again.</span>}
      </Stack>
    </Alert>
  );
}
